// Dev-only local source for installed workspace apps.
//
// An installed app (apps/* — system/crm-app, …) normally arrives as a published
// payload through fetchProjectFromSDK, so an edit under apps/ never reaches the
// shell until it is published. Listing an app's `owner/key` in
// `localStorage.workspaceAppsLocal` (a JSON array) makes _fetchAndBuildModule
// build that app's payload from its local source instead — the same object the
// app's own standalone boot sees: `config.js` (manifest v2 extras) plus the
// `symbols/index.js` sections. Parcel then bundles and watches those files, so
// an edit rebuilds the shell.
//
//   localStorage.workspaceAppsLocal = JSON.stringify(['system/crm-app'])
//   localStorage.removeItem('workspaceAppsLocal')
//
// The app must still be installed in the workspace: this only swaps where its
// code comes from, never which apps the server says are installed.
//
// One entry per apps/* folder with a package.json, keyed by its symbols.json
// `owner/key` (localWorkspaceApps.test.js keeps the two in step). Each import
// path is spelled out: Parcel only bundles a literal import() specifier.
//
// The loader map sits behind `process.env.NODE_ENV === 'development'`, which
// Parcel inlines — a production bundle drops the branch and never pulls apps/*
// in. The opt-in is read at runtime on top of that.

const LOCAL_APPS_KEY = 'workspaceAppsLocal'

const _loaders = {}
if (process.env.NODE_ENV === 'development') {
  _loaders['system/assistants-app'] = () => import('../../../apps/assistants/symbols/context.js')
  _loaders['system/bi-app'] = () => import('../../../apps/bi/symbols/context.js')
  _loaders['system/bookmarks-app'] = () => import('../../../apps/bookmarks/symbols/context.js')
  _loaders['system/cap-table-app'] = () => import('../../../apps/cap-table/symbols/context.js')
  _loaders['system/catalog-app'] = () => import('../../../apps/catalog/symbols/context.js')
  _loaders['system/cms-app'] = () => import('../../../apps/cms/symbols/context.js')
  _loaders['system/commerce-app'] = () => import('../../../apps/commerce/symbols/context.js')
  _loaders['system/crm-app'] = () => import('../../../apps/crm/symbols/context.js')
  _loaders['system/gazer-app'] = () => import('../../../apps/gazer/symbols/context.js')
  _loaders['system/growth-app'] = () => import('../../../apps/growth/symbols/context.js')
  _loaders['system/helmet-app'] = () => import('../../../apps/helmet/symbols/context.js')
  _loaders['system/hr-app'] = () => import('../../../apps/hr/symbols/context.js')
  _loaders['system/insurance-app'] = () => import('../../../apps/insurance/symbols/context.js')
  _loaders['system/invoices-app'] = () => import('../../../apps/invoices/symbols/context.js')
  _loaders['system/jobs-app'] = () => import('../../../apps/jobs/symbols/context.js')
  _loaders['system/orders-app'] = () => import('../../../apps/orders/symbols/context.js')
  _loaders['system/payroll-app'] = () => import('../../../apps/payroll/symbols/context.js')
  _loaders['system/policies-management-app'] = () =>
    import('../../../apps/policies-management/symbols/context.js')
  _loaders['system/polyglot-app'] = () => import('../../../apps/polyglot/symbols/context.js')
  _loaders['system/production-app'] = () => import('../../../apps/production/symbols/context.js')
  _loaders['system/realestate-app'] = () => import('../../../apps/realestate/symbols/context.js')
  _loaders['system/scheduling-app'] = () => import('../../../apps/scheduling/symbols/context.js')
  _loaders['system/socials-app'] = () => import('../../../apps/socials/symbols/context.js')
  _loaders['system/terminal-app'] = () => import('../../../apps/terminal/symbols/context.js')
  _loaders['system/warehouse-app'] = () => import('../../../apps/warehouse/symbols/context.js')
}

const _optedIn = () => {
  try {
    const raw = typeof localStorage !== 'undefined' && localStorage.getItem(LOCAL_APPS_KEY)
    const list = raw ? JSON.parse(raw) : null
    return Array.isArray(list) ? list.map(String) : []
  } catch (_) {
    return []
  }
}

const _announced = new Set()

// The local loader for `owner/key`, or null when the app is not opted in or has
// no local source registered (always null outside a development build).
export const localWorkspaceAppLoader = (owner, key) => {
  const id = `${owner}/${key}`
  const load = _loaders[id]
  if (!load || !_optedIn().includes(id)) return null
  return async () => {
    const context = (await load()).default || {}
    if (!_announced.has(id)) {
      _announced.add(id)
      console.warn(
        `[workspaceApps] LOCAL SOURCE ACTIVE — ${id} loads from apps/ instead of its published ` +
          `payload. Clear with localStorage.removeItem('${LOCAL_APPS_KEY}').`
      )
    }
    // Module namespaces are frozen (context.js pulls whole sections in as
    // `import * as components`), and the loader writes into the payload — copy
    // every namespace into a plain object.
    const data = {}
    for (const [name, value] of Object.entries(context)) {
      data[name] =
        Object.prototype.toString.call(value) === '[object Module]' ? { ...value } : value
    }
    return data
  }
}
